import type { Asset, Workspace } from "./types";

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

function lines(...values: Array<string | false | undefined>): string {
  return values.filter(Boolean).join("\n");
}

function assetLine(asset: Asset): string {
  return `- ${asset.name} (${asset.kind}) | ${money.format(asset.value)} | ${asset.status}`;
}

function reviewFlags(workspace: Workspace): string[] {
  const { answers } = workspace;
  return [
    answers.blendedFamily && "Blended family or children from a prior relationship",
    answers.specialNeedsBeneficiary && "Special-needs beneficiary",
    answers.businessOwnership && "Closely held business ownership",
    answers.outOfStateRealEstate && "Real estate located outside Nevada",
    answers.nonUsPerson && "Non-US person involved in the plan",
    answers.creditorProtectionGoal && "Asset-protection or creditor-planning goal",
  ].filter(Boolean) as string[];
}

export function generateDocument(workspace: Workspace, id: string): string {
  const { answers, assets } = workspace;
  const generated = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const common = lines(
    "STILLWATER TRUST WORKSPACE",
    "Generated planning draft - attorney review may be required",
    `Generated: ${generated}`,
    "",
  );

  switch (id) {
    case "trust":
      return common + lines(
        answers.trustName.toUpperCase(),
        "NEVADA REVOCABLE LIVING TRUST - PLANNING DRAFT",
        "",
        `Settlor and initial trustee: ${answers.settlorName}`,
        answers.coSettlorName && `Additional settlor: ${answers.coSettlorName}`,
        `County of residence: ${answers.county} County, ${answers.residenceState}`,
        "",
        "ARTICLE 1 - CREATION AND REVOCABILITY",
        `${answers.settlorName} establishes this revocable trust and transfers to the trustee the property described on the attached schedule. During the settlor's lifetime and capacity, the settlor may amend or revoke this trust.`,
        "",
        "ARTICLE 2 - TRUSTEE SUCCESSION",
        `The settlor serves as initial trustee. If the settlor cannot or does not serve, ${answers.successorTrustee} shall serve as successor trustee. If that person cannot serve, ${answers.backupTrustee} shall serve.`,
        "",
        "ARTICLE 3 - LIFETIME ADMINISTRATION",
        "During the settlor's lifetime, the trustee shall administer trust property for the settlor's benefit and may distribute income and principal as directed by the settlor.",
        "",
        "ARTICLE 4 - DISTRIBUTION AFTER DEATH",
        answers.distributionPlan,
        "",
        "ARTICLE 5 - ADMINISTRATIVE PROVISIONS",
        "The final attorney-approved instrument should include trustee powers, incapacity determination, spendthrift, tax, survival, dispute, governing-law, and severability provisions tailored to the completed plan.",
        "",
        "EXECUTION NOTE",
        "This workspace draft is not the final signed instrument. Confirm the final text and execution ceremony before signing.",
      );
    case "certificate":
      return common + lines(
        "CERTIFICATION OF TRUST - PLANNING DRAFT",
        "",
        `Trust name: ${answers.trustName}`,
        `Date of trust: To be inserted at execution`,
        `Settlor: ${answers.settlorName}`,
        `Current trustee: ${answers.settlorName}`,
        `Successor trustee: ${answers.successorTrustee}`,
        "Governing law: Nevada",
        "",
        "The trust has not been revoked, modified, or amended in any manner that would cause this certification to be incorrect. The trustee has authority to acquire, hold, manage, and transfer trust property.",
        "",
        "EXECUTION NOTE",
        "Confirm the certification language against the final executed trust before use.",
      );
    case "property":
      return common + lines(
        "ASSIGNMENT OF PERSONAL PROPERTY - PLANNING DRAFT",
        "",
        `${answers.settlorName}, as assignor, assigns to the trustee of ${answers.trustName} all eligible tangible personal property intended to be held in trust, subject to any exclusions confirmed before execution.`,
        "",
        "EXCLUSIONS TO REVIEW",
        "- Titled vehicles and property requiring a separate transfer process",
        "- Retirement accounts and assets transferred by beneficiary designation",
        "- Property subject to contractual transfer restrictions",
      );
    case "schedule":
      return common + lines("SCHEDULE OF TRUST PROPERTY", "", ...assets.map(assetLine));
    case "funding":
      return common + lines(
        "TRUST FUNDING ACTION PLAN",
        "",
        ...assets.map(assetLine),
        "",
        "FUNDING NOTES",
        "- Confirm deed preparation and county-recording requirements for each parcel.",
        "- Contact each financial institution for its trust-account or retitling process.",
        "- Review beneficiary designations rather than retitling retirement accounts.",
        "- Store confirmation documents with the executed trust package.",
      );
    default:
      return common + lines(
        "SUPPORTING DOCUMENT REVIEW CHECKLIST",
        "",
        `Requested document: ${id}`,
        "This supporting document requires state-specific drafting and execution review before use.",
        "",
        "COMPLEXITY FLAGS",
        ...(reviewFlags(workspace).length ? reviewFlags(workspace).map((flag) => `- ${flag}`) : ["- None currently selected"]),
      );
  }
}

export function downloadText(workspace: Workspace, id: string, title: string): void {
  const blob = new Blob([generateDocument(workspace, id)], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}.txt`;
  link.click();
  URL.revokeObjectURL(url);
}

export function getReviewFlags(workspace: Workspace): string[] {
  return reviewFlags(workspace);
}
